import { useEffect, useState } from 'react';
import NewJobModal from './NewJobModal';
import WalkaroundCapture from './WalkaroundCapture';
import { InvoiceGenerator } from '../Invoice';
import { useWorkshop } from '../hooks/useWorkshop';
import { useVertical } from '../../../context/VerticalContext';
import { errorMessage } from '../../../lib/errors';
import { clearJobResume, saveJobResume } from '../../../lib/jobDraft';

interface NewJobFlowProps {
    /** Mount this only while the flow is in use — it loads the job list. */
    onClose: () => void;
    /** Called after anything here is saved, so the host can refresh its lists. */
    onChanged?: () => void | Promise<void>;
    /** Reopen the workspace of a job created earlier instead of a blank form. */
    resumeJobId?: string | null;
}

/**
 * New Job, start to finish, in one place: the form, then the job's own
 * workspace (+ Part, + Labour, walkaround), then the invoice — without
 * leaving the screen it was started from. The dashboard and Workshop both
 * use this, so the flow cannot drift between them again (the dashboard used
 * to close on "Create Job" and send the user off to Workshop to find it).
 *
 * The job is remembered for "Resume job" (lib/jobDraft.ts) until it is
 * invoiced, so closing the modal — or the app dying — loses nothing.
 */
export default function NewJobFlow({ onClose, onChanged, resumeJobId = null }: NewJobFlowProps) {
    const { activeOrgId } = useVertical();
    const { jobs, loading, loadError, addJobItem, refresh } = useWorkshop();
    const [jobId, setJobId] = useState<string | null>(resumeJobId);
    const [modalOpen, setModalOpen] = useState(true);
    const [invoiceTarget, setInvoiceTarget] = useState<{ jobId: string; existingInvoiceId?: string } | null>(null);
    const [walkaroundJobId, setWalkaroundJobId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [resumeDropped, setResumeDropped] = useState(false);

    const job = jobId ? jobs.find((j) => j.id === jobId) : undefined;
    const inProgress = !!job && !job.locked && !job.invoice_id;

    // A job offered for resuming that has since been deleted, voided or
    // invoiced is no longer work in progress: drop it and open a blank New
    // Job. Adjusted during render (not in an effect) so the stale workspace
    // never paints; sticky, because `loading` flickers on every refresh.
    // Only on a load that succeeded: a failed one leaves the list empty, which
    // proves nothing, so the pointer is kept and the error is shown instead.
    if (resumeJobId && !resumeDropped && jobId === resumeJobId && !loading && !loadError && !inProgress) {
        setResumeDropped(true);
        setJobId(null);
    }
    useEffect(() => {
        if (resumeDropped && activeOrgId) clearJobResume(activeOrgId);
    }, [resumeDropped, activeOrgId]);

    // Remember the job for "Resume job" while it is open here and not yet
    // invoiced. Re-saved on every refresh, which keeps it fresh while in use.
    useEffect(() => {
        if (!activeOrgId || !job || !inProgress || !modalOpen) return;
        saveJobResume(activeOrgId, job.id, `${job.vehicle_vrm} \u00b7 ${job.customer_name}`);
    }, [activeOrgId, job, inProgress, modalOpen]);

    const activeResumeId = resumeDropped ? null : resumeJobId;

    const changed = async () => {
        await Promise.all([refresh(), onChanged?.()]);
    };

    const addItem = async (id: string, createdAt: string, type: 'part' | 'labour') => {
        setActionError(null);
        try {
            await addJobItem(id, createdAt, type);
            await onChanged?.();
        } catch (err) {
            setActionError(
                errorMessage(err)?.includes('FREE_TIER_LIMIT_REACHED')
                    ? 'You’ve used all your free jobs this month. Upgrade to Pro for unlimited jobs.'
                    : errorMessage(err, 'That change could not be saved. Please try again.'),
            );
        }
    };

    const closeEverything = () => {
        setModalOpen(false);
        onClose();
    };

    return (
        <>
            <NewJobModal
                // Remount when a resumed job turns out to be gone, so the form
                // opens blank (or with its draft) instead of a dead workspace.
                key={activeResumeId ? 'resume' : 'new'}
                isOpen={modalOpen}
                continueInWorkspace
                resumeJobId={activeResumeId}
                createdJob={job}
                onClose={closeEverything}
                onSuccess={async (id) => {
                    setJobId(id);
                    await changed();
                }}
                onAddPart={(id, createdAt) => addItem(id, createdAt, 'part')}
                onAddLabour={(id, createdAt) => addItem(id, createdAt, 'labour')}
                onGenerateInvoice={(id, existingInvoiceId) => {
                    // The resume pointer is left alone: backing out of the
                    // invoice keeps the job resumable, and ResumeJobBanner
                    // forgets it once an invoice actually exists.
                    setModalOpen(false);
                    setInvoiceTarget({ jobId: id, existingInvoiceId });
                }}
                onWalkaround={setWalkaroundJobId}
                onRefresh={changed}
                workspaceError={actionError ?? (loadError && !job
                    ? 'The job could not be loaded. Check your connection, then close and reopen it \u2014 nothing has been lost.'
                    : null)}
                onDismissWorkspaceError={() => setActionError(null)}
            />

            <WalkaroundCapture
                jobId={walkaroundJobId || ''}
                isOpen={!!walkaroundJobId}
                onClose={() => setWalkaroundJobId(null)}
                onSave={() => { setWalkaroundJobId(null); void changed(); }}
            />

            <InvoiceGenerator
                jobId={invoiceTarget?.jobId || ''}
                existingInvoiceId={invoiceTarget?.existingInvoiceId}
                isOpen={!!invoiceTarget}
                onClose={() => {
                    setInvoiceTarget(null);
                    void onChanged?.();
                    onClose();
                }}
            />
        </>
    );
}
